import React, { BaseSyntheticEvent, useEffect } from 'react'
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useTranslation } from 'react-i18next'
import { chevronBackOutline } from 'ionicons/icons'
import Signatures from './Signatures.page'
import { useDispatch, useSelector } from 'react-redux'
import CustomSignatures from '../../customSignature/page/CustomSignatures.page'
import { getCustomSignaturesSelector } from '../../customSignature/selectors/getCustomSignature.selectors'
import {
  resetCustomSignatureClientData,
  saveCustomSignatureToPcr,
  setSignatureType,
} from '../../customSignature/store/customSignature.actions'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { getSignatureOptionsSelector } from '../../options/selectors'
import { getSignaturesSelector } from '../selectors/getSignatures.selector'
import { clearSignatureData } from '../store/signatures.actions'
import { hasCustomSignatureUnsavedData, hasStandardSignatureUnsavedData } from '../../../utilities/signature.util'
import { showAlert } from '../../vitals/store/vitals.actions'
import { AuthenticationService } from '../../login/services/authentication.service'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { getUserSelector } from '../../user/selectors'
import { getRulesSelector } from '../../rules/selectors'
import { IRulesState } from '../../rules/store/rules.types'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import Logger from '../../../services/logger.service'

const SignaturesMainPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const rules: IRulesState = useSelector(getRulesSelector)
  const user = useSelector(getUserSelector)
  const signaturesState = useSelector(getSignaturesSelector)
  const { customSignatureClientData } = useSelector(getCustomSignaturesSelector)
  const { signatureType } = customSignatureClientData
  const signatureOptionsState = useSelector(getSignatureOptionsSelector)
  const { customSignatureOptions, isStandardSignatureConfigured } = signatureOptionsState

  const changeSectionWarningAlert: IAlertState = {
    header: t('signature.alert.changeSectionWarning.header'),
    message: t('signature.alert.changeSectionWarning.message'),
  }

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  // After closing PCR, we reset state of signatureType
  // so we need to update if isStandardSignatureConfigured is set to false (std sign is hidden)
  useEffect(() => {
    if (!isStandardSignatureConfigured && signatureType === 'standardSignature') {
      dispatch(setSignatureType('customSignature'))
    }
  }, [dispatch, isStandardSignatureConfigured, signatureType])

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (checkForSignatureUnsavedData()) {
      const changeSectionWarningAlert: IAlertState = {
        header: t('signature.alert.changeSectionWarning.header'),
        message: t('signature.alert.changeSectionWarning.message'),
        buttons: [
          { text: 'No' },
          {
            text: 'Yes',
            role: 'destructive',
            handler: () => {
              onBackClickYes()
            },
          },
        ],
      }
      dispatch(showAlert(changeSectionWarningAlert))
    } else {
      onBackClickYes()
    }
  }

  const onBackClickYes = () => {
    if (signatureType === 'customSignature') {
      rules.errors = clearValidationErrorsByPathList(rules.errors, ['state_customSignatures'])
      dispatch(resetCustomSignatureClientData())
      dispatch(saveCustomSignatureToPcr())
    } else {
      dispatch(clearSignatureData())
    }
    history?.push(TabConstants.ATTACHMENT_TAB)
  }

  const checkForSignatureUnsavedData = () => {
    return hasCustomSignatureUnsavedData(customSignatureClientData) || hasStandardSignatureUnsavedData(signaturesState)
  }

  const handleCustomsignatureToggleYes = () => {
    dispatch(clearSignatureData())
    dispatch(setSignatureType('customSignature'))
  }

  const handleStandardSignatureToggleYes = () => {
    dispatch(resetCustomSignatureClientData())
    dispatch(setSignatureType('standardSignature'))
  }

  const handleToggle = () => {
    dispatch(setSignatureType(signatureType))
    if (signatureType === 'customSignature' && hasCustomSignatureUnsavedData(customSignatureClientData)) {
      const alertButtons = [
        { text: 'No' },
        {
          text: 'Yes',
          role: 'destructive',
          handler: () => {
            handleStandardSignatureToggleYes()
          },
        },
      ]
      dispatch(showAlert({ ...changeSectionWarningAlert, buttons: alertButtons }))
    } else if (signatureType === 'standardSignature' && hasStandardSignatureUnsavedData(signaturesState)) {
      const alertButtons = [
        { text: 'No' },
        {
          text: 'Yes',
          role: 'destructive',
          handler: () => {
            handleCustomsignatureToggleYes()
          },
        },
      ]
      dispatch(showAlert({ ...changeSectionWarningAlert, buttons: alertButtons }))
    } else {
      if (signatureType === 'standardSignature') dispatch(setSignatureType('customSignature'))
      else if (signatureType === 'customSignature') dispatch(setSignatureType('standardSignature'))
    }
  }

  return (
    <IonPage id='SignaturesPage' data-testid='Signatures'>
      <PCRHeader />
      <IonHeader className='ion-no-padding on-landscape-hide'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton color='primary-alt' data-testid='BackButton' onClick={onBackClick}>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('signature.signatureTitle')}</IonTitle>
        </IonToolbar>
        {customSignatureOptions.length > 0 && isStandardSignatureConfigured && (
          <IonToolbar color='light'>
            <IonSegment
              color='dark'
              className='segment-bottom-padding'
              value={signatureType}
              onIonChange={handleToggle}>
              <IonSegmentButton data-testid='standardSignatureSegment' value={'standardSignature'}>
                <IonLabel>{t('signature.standardSignature')}</IonLabel>
              </IonSegmentButton>

              <IonSegmentButton data-testid='customSignatureSegment' value={'customSignature'}>
                <IonLabel>{t('signature.customSignature')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonToolbar>
        )}
      </IonHeader>
      {signatureType === 'standardSignature' ? <Signatures /> : <CustomSignatures />}
    </IonPage>
  )
}

export default SignaturesMainPage
